/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { Observable, of } from 'rxjs'
import { take } from 'rxjs/operators'
import { MyProfileAddressListService } from '../../services/myprofile-address-list.service'
import { AddressDto } from '../../../../../../apps/myprofile-ui/src/app/services/address/address.service'

@Component({
  selector: 'myprofile-address-list-mylanta',
  templateUrl: './myprofile-address-list-mylanta.component.html',
  host: { class: 'profile:block profile:mt-fluid-md' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileAddressListMylantaComponent implements OnInit {
  private myProfileAddressListService = inject(MyProfileAddressListService)
  private _translateService = inject(TranslateService)
  private store = inject(Store)

  @Input() instanceId?: string

  @Input() emptyListLabel?: string
  @Input() editItemLabel?: string
  @Input() token!: string

  public listDisabled$: Observable<boolean> = of(false)
  public addressIdBeingEdited$: Observable<string | null> = of(null)
  public addresses$: Observable<AddressDto[]> = of([])
  public hasAddresses$: Observable<boolean> = of(false)

  ngOnInit(): void {
    this.addresses$ = this.store.select(({ addressWidget }) => addressWidget.addresses)
    this.hasAddresses$ = this.store.select(({ addressWidget }) => addressWidget.addresses.length)

    this.listDisabled$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId as string].addressList.disabled,
    )

    this.addressIdBeingEdited$ = this.store.select(
      ({ addressWidget }) => addressWidget.widgetInstances[this.instanceId as string].addressIdBeingEdited,
    )
  }

  public editItem(item: any) {
    this.listDisabled$.pipe(take(1)).subscribe((disabled) => {
      if (!disabled) {
        this.myProfileAddressListService.editItem(item, this.instanceId as string)
      }
    })
  }

  public getUsageString(usage: 'home' | 'work') {
    const usageKeys = {
      work: 'pidpaw.editAddress.radio.addressTypeWork.label',
      home: 'pidpaw.editAddress.radio.addressTypeHome.label',
    }

    return this._translateService.instant(usageKeys[usage])
  }

  public getPrefixString(isStandard: boolean) {
    const translatedString = this._translateService.instant('pidpaw.selectAddress.text.preferredAddress')
    return isStandard ? ` | ${translatedString}` : ''
  }

  public editButtonAriaLabel(item: AddressDto): string {
    return this.myProfileAddressListService.editButtonAriaLabel(item)
  }
}
